import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, History, ImagePlus, Plus, Trash2, Upload } from "lucide-react";
import api, { formatDate, timeAgo, apiError } from "@/lib/api";
import { PageHeader, Table, StatusBadge } from "@/admin/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EMPTY = { name: "", advertiser: "", placement: "sidebar", target_url: "", duration_days: "7", price_usd: "99", starts_at: "" };
const SUGGESTED: Record<string, { d7: number; d30: number }> = {
  sidebar: { d7: 99, d30: 299 },
  homepage_banner: { d7: 249, d30: 799 },
  in_feed: { d7: 149, d30: 449 },
  article_inline: { d7: 129, d30: 399 },
  audio_brief: { d7: 199, d30: 599 },
  sector_mid: { d7: 139, d30: 429 },
  nav_menu: { d7: 174, d30: 524 },
  sector_inline: { d7: 134, d30: 414 },
  research_spotlight: { d7: 328, d30: 978 },
  premium_insight: { d7: 298, d30: 898 },
};

const RatioHint = ({ file, placement }: { file: File | null; placement?: string }) => {
  const { data: specs } = useQuery({
    queryKey: ["ads-specs"],
    queryFn: async () => (await api.get("/ads-specs")).data,
    staleTime: 10 * 60 * 1000,
  });
  const [warn, setWarn] = useState<string | null>(null);
  useEffect(() => {
    setWarn(null);
    if (!file || !file.type.startsWith("image/")) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    const spec = specs?.[placement || ""];
    if (!spec) return;
    img.onload = () => {
      const r = img.naturalWidth / img.naturalHeight;
      const problems: string[] = [];
      if (img.naturalWidth < spec.min_width) problems.push(`only ${img.naturalWidth}px wide (minimum ${spec.min_width}px)`);
      if (r < spec.aspect_min || r > spec.aspect_max) problems.push(`ratio ${r.toFixed(2)}:1 is outside the accepted ${spec.aspect_min}–${spec.aspect_max}:1 window`);
      if (problems.length) {
        const tail = placement === "premium_insight"
          ? "This slot renders full-bleed and will edge-crop — keep logos/text inside the central 70%."
          : "It will look letterboxed or cropped on-site.";
        setWarn(`This placement needs a ${spec.label} — yours is ${img.naturalWidth}×${img.naturalHeight}: ${problems.join("; ")}. ${tail}`);
      }
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [file, placement, specs]);
  if (!warn) return null;
  return (
    <p className="flex items-start gap-1.5 font-mono text-[10px] text-amber-400 mt-2" data-testid="creative-ratio-warning">
      <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> {warn}
    </p>
  );
};

export default function Ads() {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [swapAd, setSwapAd] = useState<any>(null);
  const [swapFile, setSwapFile] = useState<File | null>(null);
  const [swapSaving, setSwapSaving] = useState(false);
  const swapFileRef = useRef<HTMLInputElement>(null);

  const { data: ads } = useQuery({
    queryKey: ["admin-ads"],
    queryFn: async () => (await api.get("/admin/ads")).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-ads"] });

  const save = async () => {
    if (!file) { toast.error("Upload a banner image or video first"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/admin/ads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/ads", {
        name: form.name, advertiser: form.advertiser, placement: form.placement,
        image_url: up.data.image_url, target_url: form.target_url,
        duration_days: Number(form.duration_days), price_usd: Number(form.price_usd),
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
      });
      toast.success("Ad created & scheduled");
      setOpen(false); setForm(EMPTY); setFile(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  const toggleActive = async (ad: any, active: boolean) => {
    try { await api.patch(`/admin/ads/${ad.id}`, { active }); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (ad: any) => {
    if (!window.confirm(`Delete ad "${ad.name}"?`)) return;
    try { await api.delete(`/admin/ads/${ad.id}`); toast.success("Ad deleted"); refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const swapCreative = async () => {
    if (!swapFile) { toast.error("Choose the new banner image or video first"); return; }
    setSwapSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", swapFile);
      const up = await api.post("/admin/ads/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch(`/admin/ads/${swapAd.id}`, { image_url: up.data.image_url });
      toast.success(`Creative swapped for "${swapAd.name}" — live immediately`);
      setSwapAd(null); setSwapFile(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setSwapSaving(false);
  };

  const rollback = async (entry: any) => {
    setSwapSaving(true);
    try {
      await api.patch(`/admin/ads/${swapAd.id}`, { image_url: entry.image_url });
      toast.success(`Rolled back creative for "${swapAd.name}" — live immediately`);
      setSwapAd(null); setSwapFile(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
    setSwapSaving(false);
  };

  const CreativePreview = ({ src, className }: { src: string; className: string }) =>
    /\.(mp4|webm)$/i.test(src || "")
      ? <video src={src} muted loop autoPlay playsInline className={className} />
      : <img src={src} alt="creative" className={className} />;

  const ctr = (ad: any) => ad.impressions ? `${((ad.clicks / ad.impressions) * 100).toFixed(1)}%` : "—";
  const sug = SUGGESTED[form.placement] || SUGGESTED.sidebar;

  return (
    <div data-testid="admin-ads-page">
      <PageHeader title="Ad Manager" sub="Create, upload and schedule advertiser banners with flat 7/30-day flights"
        actions={<Button onClick={() => setOpen(true)} data-testid="add-ad-button"
          className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]"><Plus className="h-4 w-4 mr-1" /> New ad</Button>} />

      <Table headers={["Creative", "Advertiser", "Placement", "Flight", "Price", "Impr.", "Clicks", "CTR", "Active", ""]}>
        {(ads || []).map((ad: any) => (
          <tr key={ad.id} data-testid={`ad-row-${ad.id}`}>
            <td className="px-4 py-3">
              <div className="flex items-center gap-3">
                {/\.(mp4|webm)$/i.test(ad.image_url || "")
                  ? <video src={ad.image_url} muted loop autoPlay playsInline className="w-16 h-10 object-cover rounded border border-white/10" />
                  : <img src={ad.image_url} alt={ad.name} className="w-16 h-10 object-cover rounded border border-white/10" />}
                <div>
                  <p className="text-white font-medium">{ad.name}</p>
                  {ad.is_house && <StatusBadge status="house" />}
                </div>
              </div>
            </td>
            <td className="px-4 py-3 text-sm text-gray-300">{ad.advertiser}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-400 uppercase">{ad.placement}</td>
            <td className="px-4 py-3 font-mono text-[10px] text-gray-400">
              {ad.starts_at ? `${formatDate(ad.starts_at)} → ${formatDate(ad.ends_at)}` : "always on"}
              {ad.ends_at && new Date(ad.ends_at) < new Date() && (
                <span className="block mt-0.5 text-red-400 uppercase tracking-wider" data-testid={`ad-flight-ended-${ad.id}`}>
                  Ended{ad.pause_reason === "flight_ended" ? " · auto-paused" : ""}
                </span>
              )}
            </td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{ad.price_usd ? `$${ad.price_usd}` : "—"}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{(ad.impressions || 0).toLocaleString()}</td>
            <td className="px-4 py-3 font-mono text-xs text-gray-300">{(ad.clicks || 0).toLocaleString()}</td>
            <td className="px-4 py-3 font-mono text-xs text-[#00E5FF]">{ctr(ad)}</td>
            <td className="px-4 py-3">
              <Switch checked={ad.active} onCheckedChange={(v) => toggleActive(ad, v)} data-testid={`ad-toggle-${ad.id}`} />
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setSwapFile(null); setSwapAd(ad); }}
                  data-testid={`ad-swap-${ad.id}`} title="Swap creative"
                  className="text-gray-500 hover:text-[#00E5FF] h-8 w-8"><ImagePlus className="h-4 w-4" /></Button>
                {!ad.is_house && (
                  <Button size="icon" variant="ghost" onClick={() => remove(ad)} data-testid={`ad-delete-${ad.id}`}
                    className="text-gray-500 hover:text-red-400 h-8 w-8"><Trash2 className="h-4 w-4" /></Button>
                )}
              </div>
            </td>
          </tr>
        ))}
      </Table>

      <Dialog open={!!swapAd} onOpenChange={(v) => { if (!v) { setSwapAd(null); setSwapFile(null); } }}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="ad-swap-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Swap creative — {swapAd?.name}</DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-gray-500">
              Replace this campaign's graphic — placement, flight dates, pricing and stats stay unchanged.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Currently running</Label>
              {swapAd?.image_url && <CreativePreview src={swapAd.image_url} className="mt-1 rounded-md max-h-36 border border-white/10" />}
            </div>
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">New creative</Label>
              <input ref={swapFileRef} type="file" accept="image/*,video/mp4,video/webm" className="hidden"
                onChange={(e) => setSwapFile(e.target.files?.[0] || null)} data-testid="ad-swap-file" />
              <button onClick={() => swapFileRef.current?.click()} type="button" data-testid="ad-swap-upload-button"
                className="mt-1 w-full border border-dashed border-white/20 rounded-md p-4 text-sm text-gray-400 hover:border-[#00E5FF]/50 hover:text-white transition-colors flex items-center justify-center gap-2">
                <Upload className="h-4 w-4" /> {swapFile ? swapFile.name : "Choose image (max 3MB) or MP4 video (max 15MB)"}
              </button>
              {swapFile && (swapFile.type.startsWith("video/")
                ? <video src={URL.createObjectURL(swapFile)} autoPlay loop muted playsInline className="mt-2 rounded-md max-h-36 border border-[#00E5FF]/30" />
                : <img src={URL.createObjectURL(swapFile)} alt="new creative" className="mt-2 rounded-md max-h-36 border border-[#00E5FF]/30" />)}
              <RatioHint file={swapFile} placement={swapAd?.placement} />
            </div>
            <p className="font-mono text-[10px] text-gray-500">
              Placement, flight dates, pricing and stats stay unchanged — the new graphic replaces the old one everywhere it is running, immediately.
            </p>
            {(swapAd?.creative_history || []).length > 0 && (
              <div data-testid="ad-history-panel">
                <Label className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Previous creatives — one-click rollback</Label>
                <div className="mt-1 space-y-2 max-h-52 overflow-y-auto pr-1">
                  {swapAd.creative_history.map((h: any, i: number) => (
                    <div key={i} className="flex items-center gap-3 border border-white/10 rounded-md p-2" data-testid={`ad-history-row-${i}`}>
                      <CreativePreview src={h.image_url} className="w-20 h-12 object-cover rounded border border-white/10 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-[10px] text-gray-500">replaced {timeAgo(h.replaced_at)}</p>
                        {h.replaced_by && <p className="font-mono text-[10px] text-gray-600 truncate">by {h.replaced_by}</p>}
                      </div>
                      <button onClick={() => rollback(h)} disabled={swapSaving} data-testid={`ad-history-rollback-${i}`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-white/15 px-2.5 py-1.5 font-mono text-[10px] uppercase text-gray-300 hover:text-white hover:border-[#00E5FF]/40 disabled:opacity-40 transition-colors shrink-0">
                        <History className="h-3 w-3" /> Roll back
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button onClick={swapCreative} disabled={swapSaving || !swapFile} data-testid="ad-swap-save"
              className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
              {swapSaving ? "Uploading…" : "Swap creative"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">New advertiser banner</DialogTitle>
            <DialogDescription className="font-mono text-[11px] text-gray-500">
              Create a paid ad campaign — pick the placement, upload the creative and set the flight.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Campaign name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="ad-form-name" className="mt-1 bg-[#1A1A1A] border-white/10" />
            </div>
            <div>
              <Label>Advertiser</Label>
              <Input value={form.advertiser} onChange={(e) => setForm({ ...form, advertiser: e.target.value })}
                data-testid="ad-form-advertiser" className="mt-1 bg-[#1A1A1A] border-white/10" />
            </div>
            <div>
              <Label>Banner image</Label>
              <input ref={fileRef} type="file" accept="image/*,video/mp4,video/webm" className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="ad-form-file" />
              <button onClick={() => fileRef.current?.click()} type="button" data-testid="ad-form-upload-button"
                className="mt-1 w-full border border-dashed border-white/20 rounded-md p-4 text-sm text-gray-400 hover:border-[#00E5FF]/50 hover:text-white transition-colors flex items-center justify-center gap-2">
                <Upload className="h-4 w-4" /> {file ? file.name : "Choose image (max 3MB) or MP4 video (max 15MB)"}
              </button>
              {file && (file.type.startsWith("video/")
                ? <video src={URL.createObjectURL(file)} autoPlay loop muted playsInline className="mt-2 rounded-md max-h-40 border border-white/10" />
                : <img src={URL.createObjectURL(file)} alt="preview" className="mt-2 rounded-md max-h-40 border border-white/10" />)}
              <RatioHint file={file} placement={form.placement} />
            </div>
            <div>
              <Label>Click-through URL</Label>
              <Input value={form.target_url} placeholder="https://advertiser.com/landing"
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                data-testid="ad-form-target" className="mt-1 bg-[#1A1A1A] border-white/10" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Placement</Label>
                <Select value={form.placement} onValueChange={(v) => setForm({ ...form, placement: v, price_usd: String(form.duration_days === "7" ? SUGGESTED[v].d7 : SUGGESTED[v].d30) })}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="ad-form-placement"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="sidebar">Sidebar Spotlight</SelectItem>
                    <SelectItem value="homepage_banner">Homepage Banner</SelectItem>
                    <SelectItem value="in_feed">In-Feed Native</SelectItem>
                    <SelectItem value="article_inline">Article Inline</SelectItem>
                    <SelectItem value="audio_brief">Audio Brief Sponsor</SelectItem>
                    <SelectItem value="sector_mid">Sector Mid Banner</SelectItem>
                    <SelectItem value="nav_menu">Navigation Spotlight (mega menus)</SelectItem>
                    <SelectItem value="sector_inline">Sector In-Feed Native</SelectItem>
                    <SelectItem value="research_spotlight">Research Spotlight (homepage)</SelectItem>
                    <SelectItem value="premium_insight">Premium Insight (homepage)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Flight duration</Label>
                <Select value={form.duration_days} onValueChange={(v) => setForm({ ...form, duration_days: v, price_usd: String(v === "7" ? sug.d7 : sug.d30) })}>
                  <SelectTrigger className="mt-1 bg-[#1A1A1A] border-white/10" data-testid="ad-form-duration"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121212] border-white/10 text-white">
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Price (USD)</Label>
                <Input type="number" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: e.target.value })}
                  data-testid="ad-form-price" className="mt-1 bg-[#1A1A1A] border-white/10" />
                <p className="font-mono text-[10px] text-gray-500 mt-1">Suggested: ${sug.d7}/7d · ${sug.d30}/30d</p>
              </div>
              <div>
                <Label>Start date (default: now)</Label>
                <Input type="date" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })}
                  data-testid="ad-form-start" className="mt-1 bg-[#1A1A1A] border-white/10" />
              </div>
            </div>
            <Button onClick={save} disabled={saving || !form.name || !form.advertiser || !form.target_url}
              data-testid="ad-form-save" className="w-full bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0]">
              {saving ? "Uploading…" : "Create & schedule ad"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
